/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

'use client';

import {
  createContext,
  startTransition,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { usePathname, useRouter } from 'next/navigation';

// Drives a browser View Transition (`document.startViewTransition`) around a
// programmatic App Router navigation. The API must wrap the DOM commit and be
// told when it finished — which in the App Router means resolving a promise once
// the new route has rendered, which the provider detects via the pathname.
//
// Styling lives in the exported `@airasia-rn/web/styles/view-transitions.css`;
// import it into the consuming app's global stylesheet for the iOS-style slide.

export type ViewTransitionDirection = 'forward' | 'back';

interface ViewTransitionRouter {
  push: (href: string, direction?: ViewTransitionDirection) => void;
  replace: (href: string, direction?: ViewTransitionDirection) => void;
  back: (direction?: ViewTransitionDirection) => void;
}

const ViewTransitionsContext = createContext<ViewTransitionRouter | undefined>(
  undefined,
);

const supported = () =>
  typeof document !== 'undefined' &&
  typeof (document as Document & { startViewTransition?: unknown })
    .startViewTransition === 'function';

export function ViewTransitionsProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  // Holds the "transition done" resolver between starting the navigation and the
  // new route committing. Fully internal — never exposed.
  const finishRef = useRef<(() => void) | null>(null);

  // New route committed → let the in-flight transition capture the "after"
  // snapshot and play. useLayoutEffect runs before paint, so there's no flash.
  useLayoutEffect(() => {
    finishRef.current?.();
    finishRef.current = null;
  }, [pathname]);

  const run = useCallback(
    (navigate: () => void, direction: ViewTransitionDirection) => {
      if (!supported()) {
        navigate();
        return;
      }

      const root = document.documentElement;
      root.dataset.vtDirection = direction;

      const transition = (
        document as Document & {
          startViewTransition: (cb: () => Promise<void>) => {
            finished: Promise<void>;
          };
        }
      ).startViewTransition(() => {
        finishRef?.current?.();
        return new Promise<void>((resolve) => {
          finishRef.current = resolve;
          startTransition(navigate);
        });
      });

      transition.finished.finally(() => {
        delete root.dataset.vtDirection;
      });
    },
    [],
  );

  const value = useMemo<ViewTransitionRouter>(
    () => ({
      push: (href, direction = 'forward') =>
        run(() => router.push(href), direction),
      replace: (href, direction = 'back') =>
        run(() => router.replace(href), direction),
      back: (direction = 'back') => run(() => router.back(), direction),
    }),
    [run, router],
  );

  return (
    <ViewTransitionsContext.Provider value={value}>
      {children}
    </ViewTransitionsContext.Provider>
  );
}

export const useViewTransitionRouter = (): ViewTransitionRouter => {
  const ctx = useContext(ViewTransitionsContext);
  if (!ctx)
    throw new Error(
      'useViewTransitionRouter must be used inside <ViewTransitionsProvider>',
    );
  return ctx;
};
